import { useNavigate } from "react-router-dom";
import axiosClient from "../api/axiosClient";
import React, { useState, useEffect } from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import { Play, Trophy, Cpu, Globe, ChevronDown, Layers, Check } from "lucide-react";
import { toast } from "react-hot-toast";

/* ------------------------------------------------------------------ */
/*  Plan rules                                                         */
/* ------------------------------------------------------------------ */
const PLAN_RULES = {
  free: { maxTopics: 3, maxSubtopics: 4 },
  basic: { maxTopics: 4, maxSubtopics: 5 },
  standard: { maxTopics: 5, maxSubtopics: 8 },
  premium: { maxTopics: Infinity, maxSubtopics: Infinity },
};

const LEVELS = ["EASY", "MEDIUM", "HARD"];

const normalizePlan = (value) => {
  if (!value) return null;
  const v = value.toLowerCase();
  if (v.includes("demo")) return "demo";
  if (v.includes("free")) return "free";
  if (v.includes("basic")) return "basic";
  if (v.includes("standard")) return "standard";
  if (v.includes("premium")) return "premium";
  return null;
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "");
const limitText = (max) => (max === Infinity ? "" : ` / ${max}`);

const swalBase = {
  background: "#2a0733",
  color: "#fff",
  confirmButtonColor: "#fff",
  cancelButtonColor: "#5b3a66",
};

export default function QuizLandingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [plan, setPlan] = useState(null);

  const [topicsList, setTopicsList] = useState([]);
  const [topics, setTopics] = useState([]);

  const [subtopicsList, setSubtopicsList] = useState([]);
  const [selectedSubtopics, setSelectedSubtopics] = useState([]);

  const [level, setLevel] = useState([]); // ["EASY" | "MEDIUM" | "HARD"]

  const activeRules = PLAN_RULES[plan] || PLAN_RULES.free;
  const planLabel = capitalize(plan || "free");

  /* ---------------- enforce plan limits ---------------- */
  useEffect(() => {
    if (topics.length > activeRules.maxTopics) {
      setTopics(topics.slice(0, activeRules.maxTopics));
    }
    if (activeRules.maxSubtopics === 0) {
      setSelectedSubtopics([]);
    } else if (selectedSubtopics.length > activeRules.maxSubtopics) {
      setSelectedSubtopics(selectedSubtopics.slice(0, activeRules.maxSubtopics));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, topics, selectedSubtopics]);

  /* ---------------- course change: reset + read plan ---------------- */
  useEffect(() => {
    if (!selectedCourse) {
      setPlan(null);
      return;
    }

    setTopics([]);
    setSelectedSubtopics([]);
    setSubtopicsList([]);

    const stored = localStorage.getItem("coursePlanMap");
    if (!stored) {
      setPlan(null);
      return;
    }

    try {
      const coursePlanMap = JSON.parse(stored);
      const matchedKey = Object.keys(coursePlanMap).find(
        (key) => key.toLowerCase() === selectedCourse.toLowerCase(),
      );
      setPlan(matchedKey ? normalizePlan(coursePlanMap[matchedKey]) : null);
    } catch {
      setPlan(null);
    }
  }, [selectedCourse]);

  /* ---------------- load modules ---------------- */
  useEffect(() => {
    try {
      const storedCourses = JSON.parse(localStorage.getItem("activeCourses") || "[]");
      setCourses(storedCourses);

      const savedCourse = localStorage.getItem("selectedCourse");
      if (savedCourse && storedCourses.includes(savedCourse)) {
        setSelectedCourse(savedCourse);
      } else if (storedCourses.length > 0) {
        setSelectedCourse(storedCourses[0]);
      }
    } catch (error) {
      setCourses([]);
      toast.error("Couldn't load your modules. Refresh the page to try again.");
    }
  }, []);

  /* ---------------- load topics ---------------- */
  useEffect(() => {
    if (!selectedCourse) return;

    const fetchTopics = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axiosClient.get("/auth/topics", {
          params: { module: selectedCourse },
          headers: { Authorization: `Bearer ${token}` },
        });
        setTopicsList(res.data?.data || res.data || []);
        setSubtopicsList([]);
      } catch (err) {
        console.error("Topics API error:", err);
        toast.error("Couldn't load topics. Check your connection and try again.");
      }
    };

    fetchTopics();
  }, [selectedCourse]);

  /* ---------------- load subtopics ---------------- */
  useEffect(() => {
    if (!selectedCourse) return;

    if (topics.length === 0) {
      setSubtopicsList([]);
      setSelectedSubtopics([]);
      return;
    }

    const fetchSubtopics = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axiosClient.get("/auth/subtopics", {
          params: { module: selectedCourse, topics: topics.map((t) => t.value) },
          headers: { Authorization: `Bearer ${token}` },
        });

        const raw = res.data?.data || res.data || [];
        const unique = [...new Set(Array.isArray(raw) ? raw : [])];
        setSubtopicsList(unique);

        // keep only subtopics that still belong to the chosen topics
        setSelectedSubtopics((prev) => prev.filter((item) => unique.includes(item.value)));
      } catch (err) {
        console.error("Subtopics API error:", err);
        toast.error("Couldn't load subtopics. Check your connection and try again.");
      }
    };

    fetchSubtopics();
  }, [selectedCourse, topics]);

  /* ---------------- validation ---------------- */
  const missing = [];
  if (!selectedCourse) missing.push("a module");
  if (!topics.length) missing.push("at least one topic");
  if (!level.length) missing.push("a difficulty level");
  const isReady = missing.length === 0;

  const toggleLevel = (l) =>
    setLevel((prev) => (prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l]));

  /* ---------------- generate quiz ---------------- */
  const fetchQuizData1 = async () => {
    if (!isReady) return;
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");

      const payload = {
        userId,
        difficulty: level,
        module: selectedCourse,
        topics: topics.map((t) => t.value),
        subTopics: selectedSubtopics.map((s) => s.value),
      };

      const res = await axiosClient.post("/auth/generate-filter-question", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data) {
        if (payload.topics.length > 0) {
          const generatedTestName = res.data?.data?.[0]?.testName;

          if (!generatedTestName) {
            toast.error("The server didn't return a test name. Please try again.");
            return;
          }

          localStorage.setItem(`testName_${userId}`, generatedTestName);
          localStorage.setItem(
            `activeTest_${userId}`,
            JSON.stringify({
              module: selectedCourse,
              testName: generatedTestName,
              type: "CUSTOM",
              inProgress: true,
            }),
          );
          localStorage.setItem("testName", `${selectedCourse}_${Date.now()}`);
          localStorage.setItem("testMode", "CUSTOM");

          // fresh start for filtered tests
          localStorage.removeItem(`status_${userId}_${selectedCourse}`);
          localStorage.removeItem(`answers_${userId}_${selectedCourse}`);
          localStorage.removeItem(`codingStats_${userId}_${selectedCourse}`);
          localStorage.removeItem(`mcqStats_${userId}_${selectedCourse}`);
          localStorage.removeItem(`codes_${userId}_${selectedCourse}`);
          localStorage.removeItem(`outputs_${userId}_${selectedCourse}`);
        }

        toast.success("Your quiz is ready 🎉");

        // counts tests taken (reference for non-filtered tests)
        const testCount = parseInt(localStorage.getItem(`testCount_${userId}`) || "0", 10);
        localStorage.setItem(`testCount_${userId}`, testCount + 1);

        navigate("/quiz");
      }
    } catch (err) {
      console.error("API Error:", err.response?.data || err);

      const status = err.response?.status;
      const msg = err.response?.data?.message;

      if (msg?.includes("Limit Reached")) {
        Swal.fire({
          ...swalBase,
          icon: "warning",
          title: "You've reached your limit",
          text: "Upgrade your plan to generate more tests.",
          showCancelButton: true,
          confirmButtonText: "🚀 Upgrade now",
          cancelButtonText: "Not now",
        }).then((result) => {
          if (result.isConfirmed) navigate("/payment");
        });
        return;
      }

      if (msg?.includes("Trial Expired")) {
        Swal.fire({
          ...swalBase,
          icon: "warning",
          title: "Your free trial has ended",
          text: "Your 7-day trial is over. Upgrade your plan to keep taking tests.",
          confirmButtonText: "Upgrade plan",
        }).then(() => navigate("/payment"));
        return;
      }

      if (msg?.includes("ACTIVE test")) {
        const activeTest = localStorage.getItem(`activeTest_${localStorage.getItem("userId")}`);
        if (!activeTest) return;

        const match = msg.match(/module:\s*([^.]+)/);
        const fullTestName = match ? match[1].trim() : "";

        let moduleName = "";
        if (fullTestName) {
          const parts = fullTestName.split("_");
          moduleName = parts
            .slice(0, parts.length - 3)
            .join(" ")
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
        }

        Swal.fire({
          ...swalBase,
          icon: "info",
          title: "You have a test in progress",
          html: `Finish your current <b>${moduleName || "test"}</b> test before starting a new one.`,
          showCancelButton: true,
          confirmButtonText: "Resume test",
          cancelButtonText: "Cancel",
        }).then((result) => {
          if (result.isConfirmed) navigate("/quiz");
        });
        return;
      }

      if (status === 400 || msg?.toLowerCase().includes("no questions")) {
        Swal.fire({
          ...swalBase,
          icon: "error",
          title: "No matching questions",
          text: "We couldn't find new questions for these filters. Try more topics or another difficulty.",
          confirmButtonText: "Got it",
        });
        return;
      }

      toast.error(msg || "Couldn't generate your quiz. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- helper text ---------------- */
  const topicHint = !selectedCourse
    ? "Choose a module first"
    : activeRules.maxTopics === Infinity
      ? "Pick as many as you like"
      : `Up to ${activeRules.maxTopics} on your ${planLabel} plan`;

  const subtopicDisabled =
    !selectedCourse || activeRules.maxSubtopics === 0 || !subtopicsList.length;

  const subtopicHint =
    activeRules.maxSubtopics === 0
      ? "Not included in your plan"
      : !topics.length
        ? "Pick a topic to see subtopics"
        : !subtopicsList.length
          ? "Loading subtopics…"
          : activeRules.maxSubtopics === Infinity
            ? "Optional. Empty means all"
            : `Optional. Up to ${activeRules.maxSubtopics}. Empty means all`;

  return (
    <div className="qa-page">
      <style>{css}</style>

      <div className="qa-card">
        <button
          type="button"
          className="qa-dash"
          onClick={() => {
            localStorage.setItem("allowDashboardNavigation", "true");
            navigate("/front");
          }}
        >
          🏠 Dashboard
        </button>

        <div className="qa-header">
          <div className="qa-icon">
            <Trophy size={40} color="#fff" />
          </div>
          <h1>Technical Assessment Portal</h1>
          <p>Choose your module, topics and difficulty to build a custom quiz.</p>
        </div>

        <div className="qa-grid">
          {/* 1. Module */}
          <div className="qa-field">
            <label className="qa-label" htmlFor="qa-module">
              <Globe size={14} /> Module
            </label>
            <div className="qa-select-wrap">
              <select
                id="qa-module"
                value={selectedCourse}
                onChange={(e) => {
                  setSelectedCourse(e.target.value);
                  localStorage.setItem("selectedCourse", e.target.value);
                }}
              >
                <option value="" disabled>
                  {courses.length ? "Select a module" : "No modules available"}
                </option>
                {courses.map((course) => (
                  <option key={course} value={course}>
                    {course.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
              <ChevronDown size={18} color="#94a3b8" />
            </div>
            <span className="qa-help">Where your questions come from</span>
          </div>

          {/* 2. Topics */}
          <div className="qa-field">
            <label className="qa-label" htmlFor="qa-topics">
              <Layers size={14} /> Topics
              {selectedCourse && (
                <span className="qa-count">
                  {topics.length}
                  {limitText(activeRules.maxTopics)}
                </span>
              )}
            </label>
            <Select
              inputId="qa-topics"
              isMulti
              isDisabled={!selectedCourse}
              closeMenuOnSelect={false}
              placeholder="Select topics"
              noOptionsMessage={() => "No topics found"}
              options={topicsList.map((t) => ({ label: t, value: t }))}
              value={topics}
              onChange={(selected) => {
                const next = selected || [];
                if (activeRules.maxTopics !== Infinity && next.length > activeRules.maxTopics) {
                  toast.error(
                    `You can pick up to ${activeRules.maxTopics} topics on the ${planLabel} plan.`,
                  );
                  return;
                }
                setTopics(next);
              }}
              styles={selectStyles}
            />
            <span className="qa-help">{topicHint}</span>
          </div>

          {/* 3. Subtopics */}
          <div className="qa-field">
            <label className="qa-label" htmlFor="qa-subtopics">
              <Cpu size={14} /> Subtopics
              {!subtopicDisabled && (
                <span className="qa-count">
                  {selectedSubtopics.length}
                  {limitText(activeRules.maxSubtopics)}
                </span>
              )}
            </label>
            <Select
              inputId="qa-subtopics"
              isMulti
              isDisabled={subtopicDisabled}
              closeMenuOnSelect={false}
              placeholder="Select subtopics"
              noOptionsMessage={() => "No subtopics found"}
              options={subtopicsList.map((t) => ({ label: t, value: t }))}
              value={selectedSubtopics}
              onChange={(selected) => {
                const next = selected || [];
                if (
                  activeRules.maxSubtopics !== Infinity &&
                  next.length > activeRules.maxSubtopics
                ) {
                  toast.error(
                    `You can pick up to ${activeRules.maxSubtopics} subtopics on the ${planLabel} plan.`,
                  );
                  return;
                }
                setSelectedSubtopics(next);
              }}
              styles={selectStyles}
            />
            <span className="qa-help">{subtopicHint}</span>
          </div>

          {/* 4. Levels */}
          <div className="qa-field">
            <span className="qa-label" id="qa-level-label">
              <Trophy size={14} /> Levels
            </span>
            <div className="qa-levels" role="group" aria-labelledby="qa-level-label">
              {LEVELS.map((l) => {
                const on = level.includes(l);
                return (
                  <button
                    key={l}
                    type="button"
                    className="qa-level"
                    data-level={l}
                    aria-pressed={on}
                    onClick={() => toggleLevel(l)}
                  >
                    {on && <Check size={14} />}
                    {capitalize(l)}
                  </button>
                );
              })}
            </div>
            <span className="qa-help">Choose one or mix several</span>
          </div>
        </div>

        <div className="qa-action">
          <button
            type="button"
            className="qa-start"
            onClick={fetchQuizData1}
            disabled={loading || !isReady}
          >
            {loading ? <span className="qa-spinner" aria-hidden="true" /> : <Play size={22} fill="currentColor" />}
            {loading ? "Generating…" : "Start assessment now"}
          </button>

          {!isReady && !loading && (
            <p className="qa-missing" role="status">
              To continue, select {missing.join(", ")}.
            </p>
          )}
        </div>

        {selectedCourse && (
          <div className="qa-footer">
            <span>Module: {selectedCourse.replace(/_/g, " ")}</span>
            <span className="qa-sep" aria-hidden="true" />
            <span>Plan: {planLabel}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  react-select theme                                                 */
/* ------------------------------------------------------------------ */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "#1e293b",
    borderColor: state.isFocused ? "#fff" : "#334155",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(255,255,255,0.18)" : "none",
    minHeight: 52,
    borderRadius: 12,
    opacity: state.isDisabled ? 0.55 : 1,
    "&:hover": { borderColor: "#fff" },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "6px 12px",
    flexWrap: "wrap",
    maxHeight: 120,
    overflowY: "auto",
  }),
  input: (base) => ({ ...base, color: "#f8fafc" }),
  placeholder: (base) => ({ ...base, color: "#94a3b8" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, color: "#94a3b8" }),
  clearIndicator: (base) => ({ ...base, color: "#94a3b8" }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: 12,
    overflow: "hidden",
  }),
  option: (base, state) => ({
    ...base,
    cursor: "pointer",
    color: "#111827",
    backgroundColor: state.isSelected ? "#c7d2fe" : state.isFocused ? "#e0e7ff" : "#fff",
  }),
  multiValue: (base) => ({ ...base, backgroundColor: "#eef2ff", borderRadius: 6 }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#1e293b",
    fontWeight: 600,
    whiteSpace: "normal",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#475569",
    "&:hover": { backgroundColor: "#c7d2fe", color: "#1e293b" },
  }),
};


const css = `
.qa-page {
  min-height: 100vh;
  width: 100%;
  box-sizing: border-box;
  background-color: #3b0548;
  background-image:
    radial-gradient(at 0% 0%, rgba(185, 17, 236, 0.3) 0, transparent 50%),
    radial-gradient(at 100% 100%, rgba(76, 29, 149, 0.3) 0, transparent 50%);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px 16px;
  font-family: "Inter", system-ui, sans-serif;
}
.qa-page *, .qa-page *::before, .qa-page *::after { box-sizing: border-box; }

.qa-card {
  position: relative;
  width: 100%;
  max-width: 1100px;
  background: rgba(60, 4, 68, 0.7);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(171, 14, 215, 0.25);
  border-radius: 24px;
  padding: 56px 40px 40px;
  box-shadow: 0 25px 50px -12px rgba(163, 10, 153, 0.6);
  text-align: center;
  animation: qa-in 0.5s ease-out both;
}
@keyframes qa-in { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }

.qa-dash {
  position: absolute; top: 24px; right: 32px;
  background: #fff; color: #111;
  border: none; border-radius: 40px;
  padding: 10px 20px; font: inherit; font-size: 14px; font-weight: 600;
  cursor: pointer; box-shadow: 0 4px 15px rgba(0, 0, 0, 0.25);
  transition: transform .15s, box-shadow .15s;
}
.qa-dash:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(255, 255, 255, 0.3); }

.qa-header { margin-bottom: 44px; }
.qa-icon {
  width: 80px; height: 80px; border-radius: 24px;
  background: rgba(245, 158, 11, 0.18);
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 22px;
}
.qa-header h1 {
  margin: 0 0 10px; color: #fff;
  font-size: clamp(26px, 4vw, 36px); font-weight: 800; letter-spacing: -0.5px;
}
.qa-header p { margin: 0 auto; max-width: 560px; color: #cbd5e1; font-size: 17px; line-height: 1.5; }

.qa-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 22px;
  margin-bottom: 40px;
  text-align: left;
}
.qa-field { display: flex; flex-direction: column; gap: 10px; min-width: 0; }

.qa-label {
  display: flex; align-items: center; gap: 8px;
  color: #fff; font-size: 13px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.05em;
}
.qa-count {
  margin-left: auto; font-size: 12px; font-weight: 700; letter-spacing: 0;
  color: #1e293b; background: #e0e7ff; border-radius: 999px; padding: 2px 9px;
}
.qa-help { color: #cbd5e1; font-size: 13px; line-height: 1.4; }

.qa-select-wrap {
  position: relative; display: flex; align-items: center;
  height: 52px; padding: 0 14px;
  background: #1e293b; border: 1px solid #334155; border-radius: 12px;
  transition: border-color .2s, box-shadow .2s;
}
.qa-select-wrap:hover { border-color: #fff; }
.qa-select-wrap:focus-within { border-color: #fff; box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.18); }
.qa-select-wrap select {
  flex: 1; min-width: 0; height: 100%;
  background: transparent; border: none; outline: none;
  color: #f8fafc; font: inherit; font-size: 15px; font-weight: 500;
  cursor: pointer; appearance: none; text-overflow: ellipsis;
}
.qa-select-wrap select option { background: #fff; color: #000; }
.qa-select-wrap svg { flex: none; pointer-events: none; }

.qa-levels { display: flex; flex-wrap: wrap; gap: 8px; }
.qa-level {
  flex: 1 1 auto; min-height: 52px;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 0 12px;
  background: #1e293b; color: #e2e8f0;
  border: 1px solid #334155; border-radius: 12px;
  font: inherit; font-size: 14px; font-weight: 600; cursor: pointer;
  transition: background .15s, border-color .15s, color .15s;
}
.qa-level:hover { border-color: #fff; }
.qa-level[aria-pressed="true"][data-level="EASY"]   { background: #d1fae5; color: #065f46; border-color: #34d399; }
.qa-level[aria-pressed="true"][data-level="MEDIUM"] { background: #fef3c7; color: #92400e; border-color: #fbbf24; }
.qa-level[aria-pressed="true"][data-level="HARD"]   { background: #fee2e2; color: #991b1b; border-color: #f87171; }

.qa-action { display: flex; flex-direction: column; align-items: center; gap: 14px; }
.qa-start {
  display: flex; align-items: center; justify-content: center; gap: 14px;
  min-width: 320px; padding: 20px 64px;
  background: linear-gradient(135deg, #e7f1ed 0%, #fff 100%);
  color: #000; border: none; border-radius: 16px;
  font: inherit; font-size: 18px; font-weight: 800; cursor: pointer;
  transition: transform .25s, filter .25s, box-shadow .25s;
}
.qa-start:hover:not(:disabled) {
  transform: translateY(-2px); filter: brightness(1.05);
  box-shadow: 0 10px 25px rgba(255, 255, 255, 0.25);
}
.qa-start:disabled { opacity: .5; cursor: not-allowed; }
.qa-missing { margin: 0; color: #fde68a; font-size: 14px; }

.qa-spinner {
  width: 22px; height: 22px; border-radius: 50%;
  border: 3px solid rgba(0, 0, 0, 0.2); border-top-color: #000;
  animation: qa-spin 1s linear infinite;
}
@keyframes qa-spin { to { transform: rotate(360deg); } }

.qa-footer {
  margin-top: 22px; display: flex; justify-content: center; align-items: center;
  flex-wrap: wrap; gap: 14px; color: #e2e8f0; font-size: 14px; font-weight: 600;
}
.qa-sep { width: 1px; height: 14px; background: rgba(255, 255, 255, 0.4); }

.qa-page button:focus-visible,
.qa-page select:focus-visible {
  outline: 3px solid rgba(255, 255, 255, 0.7);
  outline-offset: 2px;
}

@media (max-width: 1000px) {
  .qa-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 640px) {
  .qa-card { padding: 72px 20px 28px; border-radius: 20px; }
  .qa-dash { top: 16px; right: 16px; }
  .qa-grid { grid-template-columns: 1fr; gap: 20px; }
  .qa-start { min-width: 0; width: 100%; padding: 18px 24px; }
}
@media (prefers-reduced-motion: reduce) {
  .qa-page *, .qa-page *::before, .qa-page *::after { transition: none !important; animation: none !important; }
}
`;